import React, { useState } from 'react';
import { Upload, X, Link as LinkIcon, Video } from 'lucide-react';
import { Button } from '../ui/button';

const VideoUpload = ({ value, onChange, label }) => {
  const [uploadMode, setUploadMode] = useState('url'); // 'url' or 'file'
  const [videoUrl, setVideoUrl] = useState(value || '');
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
    if (!validTypes.includes(file.type)) {
      alert('يرجى اختيار ملف فيديو (MP4, WebM, MOV, AVI)');
      return;
    }

    // Validate file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      alert('حجم الفيديو يجب أن يكون أقل من 100MB');
      return;
    }

    setUploading(true);
    setFileName(file.name);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Simulate progress (since we can't track real upload progress easily)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      const response = await fetch(`${BACKEND_URL}/api/upload/video`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) {
        throw new Error('Failed to upload video');
      }

      const data = await response.json();
      const uploadedUrl = data.url || `${BACKEND_URL}${data.file_path}`;
      setVideoUrl(uploadedUrl);
      onChange(uploadedUrl);
    } catch (error) {
      console.error('Upload error:', error);
      alert('فشل رفع الفيديو. يرجى المحاولة مرة أخرى.');
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const handleUrlChange = (url) => {
    setVideoUrl(url);
    onChange(url);
  };

  const handleRemove = () => {
    setVideoUrl('');
    setFileName('');
    onChange('');
  };

  return (
    <div className="space-y-3">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      {/* Upload Mode Selector */}
      <div className="flex gap-2 mb-3">
        <Button
          type="button"
          size="sm"
          variant={uploadMode === 'url' ? 'default' : 'outline'}
          onClick={() => setUploadMode('url')}
          className="flex-1"
        >
          <LinkIcon className="w-4 h-4 ml-2" />
          رابط خارجي
        </Button>
        <Button
          type="button"
          size="sm"
          variant={uploadMode === 'file' ? 'default' : 'outline'}
          onClick={() => setUploadMode('file')}
          className="flex-1"
        >
          <Upload className="w-4 h-4 ml-2" />
          رفع فيديو
        </Button>
      </div>

      {/* URL Input Mode */}
      {uploadMode === 'url' && (
        <input
          type="url"
          value={videoUrl}
          onChange={(e) => handleUrlChange(e.target.value)}
          placeholder="https://example.com/video.mp4"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E] focus:border-transparent"
        />
      )}

      {/* File Upload Mode */}
      {uploadMode === 'file' && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#0F388E] transition-colors">
          <input
            type="file"
            accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
            onChange={handleFileUpload}
            className="hidden"
            id="video-upload"
            disabled={uploading}
          />
          <label
            htmlFor="video-upload"
            className="cursor-pointer flex flex-col items-center"
          >
            <Upload className="w-12 h-12 text-gray-400 mb-2" />
            <span className="text-sm text-gray-600">
              {uploading ? `جاري الرفع... ${uploadProgress}%` : 'اضغط لرفع فيديو'}
            </span>
            <span className="text-xs text-gray-400 mt-1">الحد الأقصى: 100MB (MP4, WebM, MOV, AVI)</span>
          </label>
          
          {uploading && (
            <div className="mt-4 w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-[#0F388E] h-2.5 rounded-full transition-all duration-300" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          )}
        </div>
      )}

      {/* Video Preview */}
      {videoUrl && !uploading && (
        <div className="relative bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Video className="w-8 h-8 text-[#0F388E] flex-shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {fileName || 'فيديو مرفوع'}
              </p>
              <p className="text-xs text-gray-500 truncate mt-1">{videoUrl}</p>
              {videoUrl && (
                <video 
                  src={videoUrl} 
                  className="mt-3 w-full max-h-48 rounded-lg" 
                  controls
                  onError={(e) => {
                    console.error('Error loading video:', e);
                  }}
                />
              )}
            </div>
            <button
              type="button"
              onClick={handleRemove}
              className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoUpload;
